/**
 * TasksApiClient - Typed API client for task CRUD + reorder operations.
 * Uses Bearer token auth via generateAPIUrl().
 */

import type { Task, CreateTaskInput, UpdateTaskInput } from '@/types/tasks';
import { generateAPIUrl } from '@/utils';

type TasksResponse = { tasks: Task[] };
type TaskResponse = { task: Task };

/**
 * Client-side API client for task operations.
 * Requires an access token for authenticated requests.
 */
export class TasksApiClient {
  private accessToken: string;

  constructor(accessToken: string) {
    this.accessToken = accessToken;
  }

  private get headers(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.accessToken}`,
    };
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = generateAPIUrl(path);
    const response = await fetch(url, {
      ...options,
      headers: { ...this.headers, ...(options.headers as Record<string, string>) },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        (body as { error?: string }).error ?? `Request failed: ${response.status}`,
      );
    }

    return response.json() as Promise<T>;
  }

  async listTasks(listId?: string): Promise<Task[]> {
    const path = listId
      ? `/api/tasks?listId=${encodeURIComponent(listId)}`
      : '/api/tasks';
    const data = await this.request<TasksResponse>(path);
    return data.tasks;
  }

  async createTask(input: CreateTaskInput): Promise<Task> {
    const data = await this.request<TaskResponse>('/api/tasks', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return data.task;
  }

  async updateTask(taskId: string, input: Omit<UpdateTaskInput, 'id'>): Promise<Task> {
    const data = await this.request<TaskResponse>(`/api/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return data.task;
  }

  async deleteTask(taskId: string): Promise<Task> {
    const data = await this.request<TaskResponse>(`/api/tasks/${taskId}`, {
      method: 'DELETE',
    });
    return data.task;
  }

  async reorderTasks(taskIds: string[]): Promise<void> {
    await this.request<{ success: boolean }>('/api/task-reorder', {
      method: 'POST',
      body: JSON.stringify({ taskIds }),
    });
  }
}
